import React, { useMemo, useCallback, useEffect, useRef, useImperativeHandle } from "react";
import { Virtuoso } from "react-virtuoso";

import { fetchQueryMore, cloneChildren } from "utils";
import { useColumnCount } from "components/use-hooks";

const virtuosoComponents = {
    Item: React.forwardRef((p, ref) => <div {...p} ref={ref} />),
    List: React.forwardRef((p, ref) => <div {...p} ref={ref} />),
};

const chunk = (arr, size) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
        result.push(arr.slice(i, i + size));
    }
    return result;
};

const OptimizedRowGrid = React.forwardRef(({
    useWindowScroll = true,
    gridItems,
    pageInfo,
    model,
    fetchMore,
    rowClass,
    ...props
}, externalRef) => {
    const cols = useColumnCount();
    const virtuosoRef = useRef(null);

    const rows = useMemo(() => chunk(gridItems, cols), [gridItems, cols]);

    useImperativeHandle(externalRef, () => ({
        scrollToStand: (standId) => {
            if (!virtuosoRef.current || !standId) return;
            const rowIndex = rows.findIndex(row =>
                row.some(({ node }) => String(node?.id) === String(standId))
            );
            if (rowIndex !== -1) {
                virtuosoRef.current.scrollToIndex({ index: rowIndex, align: 'center', behavior: 'auto' });
            }
        }
    }), [rows]);

    useEffect(() => {
        window.dispatchEvent(new Event('scroll'));
    }, [rows]);

    const renderItem = useCallback((_, row) => (
        <div className={rowClass}>
            {row.map(({ node }) =>
                cloneChildren(props.children, { ...node, key: node.id })
            )}
        </div>
    ), [rowClass, props.children]);

    return (
        <Virtuoso
            ref={virtuosoRef}
            useWindowScroll={useWindowScroll}
            data={rows}
            overscan={100}
            className="virtuoso-scroller"
            endReached={() => fetchQueryMore(model, fetchMore, pageInfo, gridItems)}
            components={virtuosoComponents}
            itemContent={renderItem}
        />
    );
});

export default OptimizedRowGrid;
